import { Injectable } from '@angular/core';
import { IResponseError } from 'projects/common/src/app/interfaces/communication/response';
import { Subject } from 'rxjs';
import { IErrorDto, IZone } from 'src/app/modules/map/interfaces';
import { CommunicationService } from 'src/app/services/communication';
import { UserDataService } from '../../core/services/parameter';
import { IZoneEdit, IZoneTree, IZoneTreeList, IZoneType } from '../interfaces/index';
import { UrlBuilderService } from 'projects/communication/src/app/services/url-builder.service';

@Injectable({
  providedIn: 'root',
})
export class ZoneService {
  public readonly ZONE_NAME_LENGTH = 50;
  public readonly ZONE_NAME_REGEX = '[^a-z A-ZÀ-ÿ0-9-_/.]';
  public readonly REGEX_NUMBERS = /^[0-9]*$/;
  public readonly TAG_PREFIX = '451';
  public readonly TAG_LENGTH = 7;

  public zoneList: IZoneTreeList[] = [];
  public zoneTypes: IZoneType[] = [];
  public zoneTree: IZoneTree[] = [];

  public canModify: boolean;

  public showContent$ = new Subject<boolean>();
  public editZone$ = new Subject<boolean>();

  private baseUrl: string;

  constructor(
    private communicationService: CommunicationService,
    private userDataService: UserDataService,
    private urlBuilderService: UrlBuilderService,
  ) {
    this.baseUrl = this.urlBuilderService.serverBasePath;
  }

  /**
   * Get all zones types
   */
  public getAllZoneType(): Promise<IZoneType[]> {
    const url = `${this.baseUrl}/ZoneType/GetAllZoneType`;
    return this.communicationService.get(url).toPromise();
  }

  /**
   * Get zone tree levels
   */
  public getAllZoneTree(): Promise<IZoneTree[]> {
    const url = `${this.baseUrl}/Zone/GetZonesTree`;
    return this.communicationService.get(url).toPromise();
  }

  /**
   * Search zone by tag
   */
  public searchTag(tag: number): Promise<IZone[]> {
    const params = {
      searchTag: tag,
    };
    const url = `${this.baseUrl}/Zone/GetSearchTag`;
    return this.communicationService.get(url, params).toPromise();
  }

  /**
   * Create a new zone
   */
  public createZone(params): Promise<IResponseError> {
    const url = `${this.baseUrl}/Zone/ZoneInsert`;
    return this.communicationService.get(url, params).toPromise();
  }

  /**
   * Get all zones list
   */
  public getZonesTreeTable(): Promise<IZoneTreeList[]> {
    const url = `${this.baseUrl}/Zone/GetZonesTreeTable`;
    return this.communicationService.get(url).toPromise();
  }

  /**
   * Active/Deactivate zone
   */
  public activateDeactivateZone(params): Promise<IErrorDto> {
    const url = `${this.baseUrl}/Zone/ZoneActivate`;
    return this.communicationService.get(url, params).toPromise();
  }

  /**
   * Edit zone
   */
  public editZone(params: IZoneEdit): Promise<IResponseError> {
    const url = `${this.baseUrl}/Zone/ZoneEdit`;
    return this.communicationService.get(url, params).toPromise();
  }

  /**
   * Find zone by Id
   *
   * @param zoneId Zone Id
   * @returns Zone found
   */
  public findZoneById(zoneId: number): IZoneTreeList {
    return this.zoneList.find((zoneT) => zoneT.zoneId === zoneId) || null;
  }

  /**
   * Find zone type by Id
   *
   * @param typeId  Type id
   * @returns Zone type found
   */
  public findTypeZoneById(typeId: number): IZoneType {
    return this.zoneTypes.find((typeZone) => typeZone.zoneTypeId === typeId) || null;
  }

  /**
   * Find zone tree by Id
   *
   * @param typeId  Parent zone id
   * @returns Zone tree found
   */
  public findZoneTreeById(parentZoneId: number): IZoneTree {
    return this.zoneTree.find((zoneT) => zoneT.parentZoneId === parentZoneId) || null;
  }
}
